import React, { useState, useMemo, useCallback, memo } from 'react'
import {
    Box,
    Chip,
    CircularProgress,
    Collapse,
    IconButton,
    LinearProgress,
    makeStyles,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Theme,
    Tooltip,
    Typography,
} from '@material-ui/core'
import PeopleIcon from '@material-ui/icons/People'
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser'
import ReportProblemIcon from '@material-ui/icons/ReportProblem'
import WarningIcon from '@material-ui/icons/Warning'
import {
    CatalogFilterBar,
    CatalogFilterDrawer,
    PropertyTagFilterField,
    getPropertyTagChip,
} from '../../../ContractManager/CatalogFilterChrome'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp'
import {
    useOrgComplianceDashboard,
    useApartmentComplianceDashboard,
    PropertyCompliance,
    ComplianceStats,
    VendorComplianceStatus,
} from '../../../../hooks/useOrgComplianceDashboard'
import { useUserTags } from '../../../../contexts/UserTagsContext'
import { useDebounce } from '../../../../hooks'
import { formatDate } from '../../../ContractManager/utils'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(3),
    },
    header: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5),
    },
    statsGrid: {
        display: 'flex',
        flexWrap: 'nowrap',
        alignItems: 'stretch',
        gap: theme.spacing(2),
        width: '100%',
        minWidth: 0,
    },
    statCard: {
        flex: '1 1 0',
        minWidth: 0,
        padding: theme.spacing(2.5),
        borderRadius: theme.spacing(1.5),
        border: `1px solid ${theme.palette.divider}`,
        overflow: 'hidden',
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(1.5),
        },
    },
    statHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(1),
    },
    statTitle: {
        fontSize: 14,
        fontWeight: 500,
        color: theme.palette.text.secondary,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    statValue: {
        fontSize: 28,
        fontWeight: 700,
        lineHeight: 1.2,
        [theme.breakpoints.down('sm')]: {
            fontSize: 20,
        },
    },
    statDescription: {
        fontSize: 12,
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.5),
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    compliantValue: {
        color: theme.palette.success.main,
    },
    nonCompliantValue: {
        color: theme.palette.error.main,
    },
    expiringValue: {
        color: theme.palette.warning.dark,
    },
    tableCard: {
        padding: theme.spacing(3),
        borderRadius: theme.spacing(1.5),
    },
    tableHeader: {
        marginBottom: theme.spacing(2),
    },
    tableTitle: {
        fontWeight: 600,
        marginBottom: theme.spacing(0.5),
    },
    tableDescription: {
        color: theme.palette.text.secondary,
        fontSize: 14,
    },
    searchBar: {
        marginBottom: theme.spacing(2),
        maxWidth: 400,
    },
    tableContainer: {
        maxHeight: 'calc(100vh - 550px)',
    },
    headerRow: {
        '& th': {
            backgroundColor: theme.palette.grey[50],
            fontWeight: 600,
            fontSize: '13px',
            color: theme.palette.text.secondary,
        },
    },
    row: {
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    expandedRow: {
        backgroundColor: theme.palette.grey[50],
    },
    propertyCell: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    tagChip: {
        height: 20,
        fontSize: '11px',
        fontWeight: 500,
    },
    progressContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        minWidth: 150,
    },
    progressBar: {
        flex: 1,
        height: 8,
        borderRadius: 4,
        backgroundColor: theme.palette.grey[200],
    },
    progressText: {
        fontSize: 13,
        fontWeight: 500,
        minWidth: 40,
        textAlign: 'right',
    },
    statusCell: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
    },
    compliantChip: {
        backgroundColor: '#dcfce7',
        color: '#166534',
        fontWeight: 500,
        fontSize: 12,
    },
    actionChip: {
        backgroundColor: '#fee2e2',
        color: '#991b1b',
        fontWeight: 500,
        fontSize: 12,
    },
    expiringChip: {
        backgroundColor: '#fef9c3',
        color: '#854d0e',
        fontWeight: 500,
        fontSize: 12,
    },
    pendingChip: {
        backgroundColor: '#fef9c3',
        color: '#854d0e',
        fontWeight: 500,
        fontSize: 12,
    },
    noCOIChip: {
        backgroundColor: '#fef2f2',
        color: '#7f1d1d',
        fontWeight: 500,
        fontSize: 12,
    },
    emptyState: {
        textAlign: 'center',
        padding: theme.spacing(4),
        color: theme.palette.text.secondary,
    },
    // Accordion/Collapse styles
    collapseCell: {
        paddingBottom: 0,
        paddingTop: 0,
        backgroundColor: theme.palette.grey[50],
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    vendorListContainer: {
        padding: theme.spacing(2, 0),
    },
    vendorTable: {
        '& th': {
            backgroundColor: theme.palette.grey[100],
            fontWeight: 600,
            fontSize: 12,
            color: theme.palette.text.secondary,
            padding: theme.spacing(1, 2),
            borderBottom: `1px solid ${theme.palette.divider}`,
        },
        '& td': {
            padding: theme.spacing(1.5, 2),
            fontSize: 13,
            borderBottom: `1px solid ${theme.palette.divider}`,
        },
        '& tr:last-child td': {
            borderBottom: 'none',
        },
    },
    vendorNameCell: {
        fontWeight: 500,
    },
    issueText: {
        fontSize: 12,
        color: theme.palette.error.main,
        cursor: 'pointer',
    },
    issueIcon: {
        fontSize: 14,
        marginTop: 1,
    },
    loadingVendors: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(3),
    },
    noVendors: {
        padding: theme.spacing(2),
        textAlign: 'center',
        color: theme.palette.text.secondary,
        fontSize: 13,
    },
}))

interface OrganizationComplianceProps {
    organization: number | null
    selectedTagId: number | null
    onSelectedTagIdChange: (value: number | null) => void
}

// Status chip component
const StatusChip = memo(
    ({
        status,
        classes,
    }: {
        status: VendorComplianceStatus
        classes: ReturnType<typeof useStyles>
    }) => {
        switch (status) {
            case 'COMPLIANT':
                return (
                    <Chip
                        label="Compliant"
                        size="small"
                        className={classes.compliantChip}
                    />
                )
            case 'NON_COMPLIANT':
                return (
                    <Chip
                        label="Non-Compliant"
                        size="small"
                        className={classes.actionChip}
                    />
                )
            case 'EXPIRED':
                return (
                    <Chip
                        label="Expired"
                        size="small"
                        className={classes.actionChip}
                    />
                )
            case 'EXPIRING_SOON':
                return (
                    <Chip
                        label="Expiring Soon"
                        size="small"
                        className={classes.expiringChip}
                    />
                )
            case 'NO_COI':
                return (
                    <Chip
                        label="No COI"
                        size="small"
                        className={classes.noCOIChip}
                    />
                )
            default:
                return (
                    <Chip
                        label="Needs approval"
                        size="small"
                        className={classes.pendingChip}
                    />
                )
        }
    },
)

// Expanded row content component
const ExpandedRowContent = ({
    apartmentId,
    classes,
}: {
    apartmentId: number
    classes: ReturnType<typeof useStyles>
}) => {
    const { vendors, isLoading } = useApartmentComplianceDashboard({
        apartmentId,
    })

    if (isLoading) {
        return (
            <Box className={classes.loadingVendors}>
                <CircularProgress size={24} />
            </Box>
        )
    }

    if (vendors.length === 0) {
        return (
            <Box className={classes.noVendors}>
                <Typography variant="body2">
                    No vendors associated with this property.
                </Typography>
            </Box>
        )
    }

    return (
        <Box className={classes.vendorListContainer}>
            <Table size="small" className={classes.vendorTable}>
                <TableHead>
                    <TableRow>
                        <TableCell>Vendor</TableCell>
                        <TableCell>Expiration</TableCell>
                        <TableCell>Issues</TableCell>
                        <TableCell align="right">Status</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {vendors.map((vendor) => {
                        const hasIssues =
                            vendor.compliance_issues &&
                            vendor.compliance_issues.length > 0

                        return (
                            <TableRow key={vendor.vendor_id}>
                                <TableCell className={classes.vendorNameCell}>
                                    {vendor.vendor_name}
                                </TableCell>
                                <TableCell>
                                    {vendor.earliest_expiration
                                        ? formatDate(vendor.earliest_expiration)
                                        : '—'}
                                </TableCell>
                                <TableCell>
                                    {hasIssues ? (
                                        <Tooltip
                                            title={
                                                <Box>
                                                    {vendor.compliance_issues.map(
                                                        (issue, idx) => (
                                                            <Typography
                                                                key={idx}
                                                                variant="body2"
                                                                style={{
                                                                    marginBottom: 4,
                                                                }}
                                                            >
                                                                • {issue}
                                                            </Typography>
                                                        ),
                                                    )}
                                                </Box>
                                            }
                                            arrow
                                            placement="top"
                                        >
                                            <span className={classes.issueText}>
                                                {
                                                    vendor.compliance_issues
                                                        .length
                                                }{' '}
                                                issue
                                                {vendor.compliance_issues
                                                    .length !== 1
                                                    ? 's'
                                                    : ''}
                                            </span>
                                        </Tooltip>
                                    ) : (
                                        <span style={{ color: '#9ca3af' }}>
                                            —
                                        </span>
                                    )}
                                </TableCell>
                                <TableCell align="right">
                                    <StatusChip
                                        status={vendor.status}
                                        classes={classes}
                                    />
                                </TableCell>
                            </TableRow>
                        )
                    })}
                </TableBody>
            </Table>
        </Box>
    )
}

// Memoized stats cards component
const StatsCards = memo(
    ({
        stats,
        classes,
    }: {
        stats: ComplianceStats | null
        classes: ReturnType<typeof useStyles>
    }) => (
        <Box className={classes.statsGrid}>
            <Paper className={classes.statCard} elevation={0}>
                <Box className={classes.statHeader}>
                    <Typography className={classes.statTitle}>
                        Total Vendors
                    </Typography>
                    <PeopleIcon style={{ fontSize: 20, color: '#9ca3af' }} />
                </Box>
                <Typography className={classes.statValue}>
                    {stats?.total_vendors || 0}
                </Typography>
                <Typography className={classes.statDescription}>
                    Active in system
                </Typography>
            </Paper>

            <Paper className={classes.statCard} elevation={0}>
                <Box className={classes.statHeader}>
                    <Typography className={classes.statTitle}>
                        Compliant
                    </Typography>
                    <VerifiedUserIcon
                        style={{ fontSize: 20, color: '#22c55e' }}
                    />
                </Box>
                <Typography
                    className={`${classes.statValue} ${classes.compliantValue}`}
                >
                    {stats?.compliant || 0}
                </Typography>
                <Typography className={classes.statDescription}>
                    Fully insured
                </Typography>
            </Paper>

            <Paper className={classes.statCard} elevation={0}>
                <Box className={classes.statHeader}>
                    <Typography className={classes.statTitle}>
                        Action Required
                    </Typography>
                    <ReportProblemIcon
                        style={{ fontSize: 20, color: '#ef4444' }}
                    />
                </Box>
                <Typography
                    className={`${classes.statValue} ${classes.nonCompliantValue}`}
                >
                    {stats?.action_required || 0}
                </Typography>
                <Typography className={classes.statDescription}>
                    Missing or invalid COI
                </Typography>
            </Paper>

            <Paper className={classes.statCard} elevation={0}>
                <Box className={classes.statHeader}>
                    <Typography className={classes.statTitle}>
                        Expiring Soon
                    </Typography>
                    <WarningIcon style={{ fontSize: 20, color: '#eab308' }} />
                </Box>
                <Typography
                    className={`${classes.statValue} ${classes.expiringValue}`}
                >
                    {stats?.expiring_soon || 0}
                </Typography>
                <Typography className={classes.statDescription}>
                    Within 30 days
                </Typography>
            </Paper>
        </Box>
    ),
)

// Property row component with expand/collapse
const PropertyRow = memo(
    ({
        property,
        isExpanded,
        onToggle,
        getCompliancePercentage,
        getProgressColor,
        getTagsForProperty,
        classes,
    }: {
        property: PropertyCompliance
        isExpanded: boolean
        onToggle: () => void
        getCompliancePercentage: (property: PropertyCompliance) => number
        getProgressColor: (percentage: number) => string
        getTagsForProperty: (
            apartmentId: number,
        ) => Array<{
            id: number
            name: string
            color: string
        }>
        classes: ReturnType<typeof useStyles>
    }) => {
        const percentage = getCompliancePercentage(property)
        const progressColor = getProgressColor(percentage)
        const tags = getTagsForProperty(property.apartment_id)

        return (
            <>
                <TableRow
                    className={`${classes.row} ${
                        isExpanded ? classes.expandedRow : ''
                    }`}
                    onClick={onToggle}
                >
                    <TableCell>
                        <Box display="flex" alignItems="center">
                            <IconButton size="small" style={{ marginRight: 8 }}>
                                {isExpanded ? (
                                    <KeyboardArrowUpIcon />
                                ) : (
                                    <KeyboardArrowDownIcon />
                                )}
                            </IconButton>
                            <Box className={classes.propertyCell}>
                                <span>{property.apartment_name}</span>
                                {tags.length > 0 && (
                                    <Box
                                        display="flex"
                                        flexWrap="wrap"
                                        style={{ gap: 4 }}
                                    >
                                        {tags.map((tag) => (
                                            <Chip
                                                key={tag.id}
                                                label={tag.name}
                                                size="small"
                                                className={classes.tagChip}
                                                style={{
                                                    backgroundColor: tag.color,
                                                    color: 'white',
                                                }}
                                            />
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </TableCell>
                    <TableCell align="center">
                        {property.total_vendors}
                    </TableCell>
                    <TableCell>
                        <Box className={classes.progressContainer}>
                            <LinearProgress
                                variant="determinate"
                                value={percentage}
                                className={classes.progressBar}
                                style={{ backgroundColor: '#e5e7eb' }}
                            />
                            <Typography
                                className={classes.progressText}
                                style={{ color: progressColor }}
                            >
                                {percentage}%
                            </Typography>
                        </Box>
                    </TableCell>
                    <TableCell align="center">
                        <Chip
                            label={property.compliant}
                            size="small"
                            className={classes.compliantChip}
                        />
                    </TableCell>
                    <TableCell align="center">
                        {property.action_required > 0 ? (
                            <Chip
                                label={property.action_required}
                                size="small"
                                className={classes.actionChip}
                            />
                        ) : (
                            <Typography variant="body2" color="textSecondary">
                                0
                            </Typography>
                        )}
                    </TableCell>
                    <TableCell align="center">
                        {property.expiring_soon > 0 ? (
                            <Chip
                                label={property.expiring_soon}
                                size="small"
                                className={classes.expiringChip}
                            />
                        ) : (
                            <Typography variant="body2" color="textSecondary">
                                0
                            </Typography>
                        )}
                    </TableCell>
                </TableRow>
                <TableRow>
                    <TableCell
                        colSpan={6}
                        className={classes.collapseCell}
                        style={{ padding: 0 }}
                    >
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <ExpandedRowContent
                                apartmentId={property.apartment_id}
                                classes={classes}
                            />
                        </Collapse>
                    </TableCell>
                </TableRow>
            </>
        )
    },
)

export const OrganizationCompliance: React.FC<OrganizationComplianceProps> = ({
    organization,
    selectedTagId,
    onSelectedTagIdChange,
}) => {
    const classes = useStyles()
    const { userTags } = useUserTags()
    const { properties, totals, isLoading } = useOrgComplianceDashboard({
        organization,
    })
    const [searchTerm, setSearchTerm] = useState('')
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false)
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const [expandedPropertyId, setExpandedPropertyId] = useState<number | null>(
        null,
    )

    // Debounce search term for performance
    const debouncedSearchTerm = useDebounce(searchTerm, 200)

    // Get all tags for a property (a property can have multiple tags)
    const getTagsForProperty = useCallback(
        (apartmentId: number) => {
            return userTags.filter((tag) =>
                tag.apartments.includes(apartmentId),
            )
        },
        [userTags],
    )

    // Memoize filtered properties using debounced search and tag filter
    const filteredProperties = useMemo(() => {
        let filtered = properties

        // Filter by tag
        if (selectedTagId !== null) {
            const selectedTag = userTags.find((tag) => tag.id === selectedTagId)
            if (selectedTag) {
                filtered = filtered.filter((property) =>
                    selectedTag.apartments.includes(property.apartment_id),
                )
            }
        }

        // Filter by search
        if (debouncedSearchTerm.trim()) {
            const search = debouncedSearchTerm.toLowerCase()
            filtered = filtered.filter((property) =>
                property.apartment_name?.toLowerCase().includes(search),
            )
        }

        return filtered
    }, [properties, debouncedSearchTerm, selectedTagId, userTags])

    // Paginated properties
    const paginatedProperties = useMemo(() => {
        const start = page * rowsPerPage
        const end = start + rowsPerPage
        return filteredProperties.slice(start, end)
    }, [filteredProperties, page, rowsPerPage])

    // Calculate compliance percentage
    const getCompliancePercentage = useCallback(
        (property: PropertyCompliance): number => {
            if (property.total_vendors === 0) return 100
            return Math.round(
                (property.compliant / property.total_vendors) * 100,
            )
        },
        [],
    )

    // Get progress bar color based on percentage
    const getProgressColor = useCallback((percentage: number): string => {
        if (percentage >= 80) return '#22c55e'
        if (percentage >= 50) return '#eab308'
        return '#ef4444'
    }, [])

    const tagFilterChips = useMemo(() => {
        const chip = getPropertyTagChip(selectedTagId, userTags, () =>
            onSelectedTagIdChange(null),
        )
        return chip ? [chip] : []
    }, [selectedTagId, userTags, onSelectedTagIdChange])

    const handleChangePage = (
        _event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    const handleToggleExpand = useCallback((apartmentId: number) => {
        setExpandedPropertyId((prev) =>
            prev === apartmentId ? null : apartmentId,
        )
    }, [])

    if (isLoading) {
        return (
            <Box
                className={classes.root}
                display="flex"
                alignItems="center"
                justifyContent="center"
                minHeight={300}
            >
                <CircularProgress />
            </Box>
        )
    }

    return (
        <Box className={classes.root}>
            <Box className={classes.header}>
                <Typography variant="h6">Compliance Dashboard</Typography>
                <Typography variant="body2" color="textSecondary">
                    Monitor insurance risk and readiness across all properties.
                    Click on a property to view vendor details.
                </Typography>
            </Box>

            <StatsCards stats={totals} classes={classes} />

            <Paper className={classes.tableCard} elevation={0}>
                <Box className={classes.tableHeader}>
                    <Typography variant="h6" className={classes.tableTitle}>
                        Property Compliance Status
                    </Typography>
                    <Typography className={classes.tableDescription}>
                        Compliance overview by property. Click to expand and see
                        vendor details.
                    </Typography>
                </Box>

                <CatalogFilterBar
                    searchPlaceholder="Search properties..."
                    searchValue={searchTerm}
                    onSearchChange={(value) => {
                        setSearchTerm(value)
                        setPage(0)
                    }}
                    onOpenFilters={() => setFilterDrawerOpen(true)}
                    chips={tagFilterChips}
                    onClearAll={
                        selectedTagId != null
                            ? () => onSelectedTagIdChange(null)
                            : undefined
                    }
                    resultLabel={`${filteredProperties.length} propert${
                        filteredProperties.length === 1 ? 'y' : 'ies'
                    }`}
                />
                <CatalogFilterDrawer
                    open={filterDrawerOpen}
                    onClose={() => setFilterDrawerOpen(false)}
                    title="Compliance filters"
                    activeCount={selectedTagId != null ? 1 : 0}
                    onClear={() => onSelectedTagIdChange(null)}
                >
                    <PropertyTagFilterField
                        value={selectedTagId}
                        onChange={(value) => {
                            onSelectedTagIdChange(value)
                            setPage(0)
                        }}
                    />
                </CatalogFilterDrawer>

                {filteredProperties.length === 0 ? (
                    <Box className={classes.emptyState}>
                        <Typography>
                            {searchTerm
                                ? 'No properties match your search.'
                                : 'No properties found.'}
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <TableContainer className={classes.tableContainer}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow className={classes.headerRow}>
                                        <TableCell>Property</TableCell>
                                        <TableCell align="center">
                                            Total Vendors
                                        </TableCell>
                                        <TableCell>Compliance Rate</TableCell>
                                        <TableCell align="center">
                                            Compliant
                                        </TableCell>
                                        <TableCell align="center">
                                            Action Required
                                        </TableCell>
                                        <TableCell align="center">
                                            Expiring Soon
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginatedProperties.map((property) => (
                                        <PropertyRow
                                            key={property.apartment_id}
                                            property={property}
                                            isExpanded={
                                                expandedPropertyId ===
                                                property.apartment_id
                                            }
                                            onToggle={() =>
                                                handleToggleExpand(
                                                    property.apartment_id,
                                                )
                                            }
                                            getCompliancePercentage={
                                                getCompliancePercentage
                                            }
                                            getProgressColor={getProgressColor}
                                            getTagsForProperty={
                                                getTagsForProperty
                                            }
                                            classes={classes}
                                        />
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={filteredProperties.length}
                            page={page}
                            onChangePage={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onChangeRowsPerPage={handleChangeRowsPerPage}
                            rowsPerPageOptions={[5, 10, 25, 50]}
                        />
                    </>
                )}
            </Paper>
        </Box>
    )
}
